import { useDispatch, useSelector } from 'react-redux'
import { selectAuthCommonData, selectAuthData, selectEnergyAuthCommonData } from '@/containers/Login/AuthSelectors'
import { useTranslation } from 'react-i18next'
import useFormStatus from '@/containers/SubscriptionFormPageNew/SubscriptionForm/useFormStatus'
import {
  updateActiveAddress,
  updateAddressData,
  updateAddressesConfigData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import {
  selectActiveAddress,
  selectAddressesData,
  selectCurrentAddressData,
  selectCurrentAddressErrors,
  selectPreferencesContactSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { modulesConfig } from '../../config'
import { lazy } from 'react'
import { subscriptionFormActionsV2 } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormV2Sagas'
import { selectAddressesConfigData } from '../../SubscriptionForm/SubscriptionFormSelectors'
import useBusinessType from '@/shared/hooks/useBusinessType'
import { HOUSING_TYPE_APARTMENT } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Common/constants'
import { useSnackbar } from 'baseui/snackbar'
import useRetrieveActiveContractIdAndType from '../../Layouts/useRetrieveActiveContractIdAndType'

const AddressSearchComponents = {
  fr: lazy(() => import('./Fields/FR/AddressSearch')),
  it: lazy(() => import('./Fields/IT/AddressSearch')),
  default: lazy(() => import('./Fields/Default/AddressSearch')),
}

const useAddress = () => {
  const { t } = useTranslation()
  const { enqueue } = useSnackbar()
  const dispatch = useDispatch()
  const { country } = useSelector(selectAuthData)
  const { activeTab } = useRetrieveActiveContractIdAndType()
  const { residenceOptions, houseTypeOptions, heatingOptions, hotwaterOptions, cookingOptions } =
    useSelector(selectAuthCommonData)
  const { tenantTypes, businessTenantTypes } = useSelector(selectEnergyAuthCommonData)
  const { isEditable } = useFormStatus()
  const {
    residenceType,
    tenantType,
    housingType,
    additionalInformation,
    streetName = '',
    streetNumber = '',
    city = '',
    zipcode = '',
    floor,
    staircase,
    apartmentNumber,
    province,
    customInput = false,
    isResident,
    surface,
    heatingEnergy,
    cookingEnergy,
    hotwaterEnergy,
    energyLandscape = {},
  } = useSelector(selectCurrentAddressData)
  const { clientType } = useSelector(selectPreferencesContactSubscriptionData)
  const { isAllowedEnergyVertical } = useBusinessType({})

  const activeAddress = useSelector(selectActiveAddress)
  const addresses = useSelector(selectAddressesData)
  const {
    addressIsSaving,
    isCalculatingConsumption = false,
    showAddressFields,
    showEnergyLandscape,
  } = useSelector(selectAddressesConfigData)
  const errors = useSelector(selectCurrentAddressErrors)
  const currentAddressData = useSelector(selectCurrentAddressData)

  const streetPart = [streetNumber, streetName].filter(Boolean).join(' ')
  const cityPart = [zipcode, city].filter(Boolean).join(' ')

  const addressLabel = [streetPart, cityPart].filter(Boolean).join(', ')

  const updateAddressHandlers = {
    it: addressData => {
      dispatch(updateAddressData({ data: addressData, id: activeAddress }))
      const resolvedZipcode = addressData.zipcode ?? zipcode
      const resolvedCity = addressData.city ?? city
      if (customInput && resolvedZipcode && resolvedCity) {
        dispatch({ type: subscriptionFormActionsV2.GET_COMPARATOR_CITY_ID })
      }
    },
    default: addressData => {
      dispatch(updateAddressData({ data: addressData, id: activeAddress }))
    },
  }

  const updateAddress = addressData => {
    const handler = updateAddressHandlers[country] ?? updateAddressHandlers.default
    handler(addressData)
  }

  const updateAddressEnergyLandscape = energyLandscapeData => {
    dispatch(
      updateAddressData({
        data: { energyLandscape: { ...energyLandscape, ...energyLandscapeData } },
        id: activeAddress,
      }),
    )
  }

  const updateAddressesConfigHandler = data => {
    dispatch(updateAddressesConfigData(data))
  }

  const updateActiveAddressHandler = id => {
    dispatch(updateActiveAddress(id))
    if (
      country === 'fr' &&
      id &&
      id !== 'new' &&
      Object.keys(addresses[id]?.cityEligibility ?? {}).length === 0 &&
      addresses[id]?.inseeCode
    ) {
      dispatch({
        type: subscriptionFormActionsV2.CITY_ELIGIBILITY_CHECK_V2,
        inseeCode: addresses[id].inseeCode,
        addressId: id,
        offersParams: {},
        loadOffersUrl: '',
        loadOfferConfigurationUrl: '',
        businessVerticalType: '',
        activeTab,
      })
    }
  }
  const calculateZipcodeValidationWarning = () => {
    const { zipcode, electricityMeter = {}, gasMeter = {} } = currentAddressData || {}
    if (!zipcode) return false

    const normalizedZipcode = zipcode.toString().trim()

    const electricityZipcode = electricityMeter.pdlEligibilityData?.elec?.technicalData?.address?.zipcode
      ?.toString()
      .trim()
    if (electricityZipcode && electricityZipcode !== normalizedZipcode) {
      return true
    }

    const gasZipcode = gasMeter.pceEligibilityData?.gas?.deliveryPoint?.address?.postalCode?.toString().trim()
    if (gasZipcode && gasZipcode !== normalizedZipcode) {
      return true
    }

    return false
  }

  const hasZipcodeWarning = calculateZipcodeValidationWarning()
  const isItalyBusiness = country === 'it' && clientType === 'pro'
  const residenceOptionsArray = []
  const houseTypeOptionsArray = []
  const tenantTypeOptionsArray =
    tenantTypes?.map(option => ({
      id: option,
      label: t(`subscriptionForm:address_module.tenant_types.${option}`),
    })) ?? []
  const businessTenantTypeOptionsArray =
    businessTenantTypes?.map(option => ({
      id: option,
      label: t(`subscriptionForm:address_module.tenant_types.${option}`),
    })) ?? []
  houseTypeOptions?.forEach(option => {
    houseTypeOptionsArray.push({
      [option]: option,
    })
  })

  residenceOptions?.forEach(option => {
    residenceOptionsArray.push({
      [option]: option,
    })
  })

  const addressesList = []
  Object.entries(addresses).forEach(([id, address]) => {
    if (id === 'config' || id === 'new') return
    addressesList[id] = address.label
  })

  const triggerCalculateConsumption = () => {
    dispatch({
      type: subscriptionFormActionsV2.CALCULATE_CONSUMPTION,
    })
  }

  const saveActiveAddress = () => {
    dispatch({
      type: subscriptionFormActionsV2.SAVE_ADDRESS,
      enqueue,
      successMessage: t('subscriptionForm:address_v2.address_saved_success'),
    })
  }

  const shouldShowMeterInformation = activeAddress || !addressesList.length
  const config = modulesConfig[country].address ?? {}
  const LocalizedAddressSearchComponent = AddressSearchComponents[country] ?? AddressSearchComponents.default

  return {
    t,
    residenceType,
    tenantType,
    housingType,
    additionalInformation,
    streetName,
    streetNumber,
    city,
    zipcode,
    floor,
    staircase,
    apartmentNumber,
    province,
    customInput,
    isResident,
    surface,
    heatingEnergy,
    heatingOptions,
    cookingEnergy,
    cookingOptions,
    hotwaterEnergy,
    hotwaterOptions,
    energyLandscape,
    addresses,
    addressesList,
    activeAddress,
    showIsResident: config.isResident && !isItalyBusiness,
    showResidenceType: residenceOptionsArray.length > 0 && !isItalyBusiness,
    showProvince: config.province ?? true,
    optionalResidenceFields: config.optionalResidenceFields ?? false,
    showFloor: config.floor ?? true,
    showStaircase: config.staircase ?? false,
    showApartmentNumber: config.apartmentNumber ?? true,
    energyLandscapeAvailable: config.energyLandscape ?? true,
    isApartment: housingType === HOUSING_TYPE_APARTMENT,
    residenceOptionsArray,
    houseTypeOptionsArray,
    houseTypeOptions,
    tenantTypeOptionsArray: isItalyBusiness ? businessTenantTypeOptionsArray : tenantTypeOptionsArray,
    updateAddress,
    updateActiveAddressHandler,
    updateAddressesConfigHandler,
    updateAddressEnergyLandscape,
    saveActiveAddress,
    shouldShowMeterInformation,
    errors,
    addressIsSaving,
    LocalizedAddressSearchComponent,
    showAddressFields,
    showEnergyLandscape: showEnergyLandscape || (energyLandscape && Object.entries(energyLandscape).length > 0),
    showConsumption: isAllowedEnergyVertical && config.consumption,
    isAllowedEnergyVertical,
    isEditable,
    hasZipcodeWarning,
    isCalculatingConsumption,
    triggerCalculateConsumption,
    addressLabel,
  }
}

export default useAddress
